import {
    NetworkError,
    generateRouteDefinition,
    makeCanonicalUrl,
} from '@jet-app/music-classical/utilities';
import {
    makeSearchPageIntent,
    type SearchPageIntent,
} from './SearchPageIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import type {
    SearchPage,
    SearchSuggestions,
} from '@jet-app/music-classical/models/pages/search';
import { IntentKind } from '@jet-app/music-classical/intents';
import { requestViewPage } from '@jet-app/music-classical/api/request-view-page';
import { renderSearchPage } from './renderSearchPage';
import { renderSearchSuggestions } from './renderSearchSuggestions';
import { makeRefreshPageIntent } from '../refresh-page/RefreshPageIntent';

const canonicalUrlRule = {
    path: '/{storefront}/search',
    query: ['term?'],
};

export const SearchPageIntentController: IntentController<SearchPageIntent> &
    RouteProvider = {
    $intentKind: IntentKind.SEARCH_PAGE,

    routes: generateRouteDefinition<SearchPageIntent>(
        canonicalUrlRule,
        makeSearchPageIntent,
    ),

    async perform(
        intent: SearchPageIntent,
        objectGraph: ObjectGraph,
    ): Promise<SearchPage | SearchSuggestions> {
        const { term, isSearchSuggestions } = intent;
        const canonicalURL = makeCanonicalUrl(
            canonicalUrlRule,
            intent,
            objectGraph,
        );

        const apiResponse = await requestViewPage(
            `search${term ? `?q=${term}` : ''}`,
            objectGraph,
            null,
            true,
        );

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        const page = isSearchSuggestions
            ? renderSearchSuggestions(apiResponse, objectGraph)
            : renderSearchPage(canonicalURL, apiResponse, term, objectGraph);

        return {
            ...page,
            invalidationRules: {
                eventTriggers: [
                    {
                        events: [
                            {
                                name: 'recentCatalogSearchesChanged',
                            },
                        ],
                        intent: makeRefreshPageIntent(
                            intent,
                            'recentCatalogSearchesChanged',
                        ),
                    },
                ],
            },
        };
    },
};
